import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import { postponePatientOutreachBulk } from "actions/patient-outreaches";
import ActionModal from "./action-modal";
import SelectedPatientsList from "./selected-patients-list";
import BulkResultsSummary from "./bulk-results-summary";

const pad = n => String(n).padStart(2, "0");
const toIsoDate = d =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

const BulkPostponeModal = ({ selectedRows, onClose, onComplete }) => {
  const dispatch = useDispatch();
  const { todayIso, tomorrowIso, maxIso } = useMemo(() => {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);
    const max = new Date(today);
    max.setDate(today.getDate() + 365);
    return {
      todayIso: toIsoDate(today),
      tomorrowIso: toIsoDate(tomorrow),
      maxIso: toIsoDate(max)
    };
  }, []);

  const [newDate, setNewDate] = useState(tomorrowIso);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      const r = await dispatch(
        postponePatientOutreachBulk(
          selectedRows.map(s => s.patientGuid),
          newDate
        )
      );
      setResult(r);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (result && onComplete) onComplete();
    onClose();
  };

  return (
    <ActionModal
      title={`Bulk postpone (${selectedRows.length})`}
      onClose={handleClose}
    >
      {!result ? (
        <>
          <p>
            Postpone outreach for the following{" "}
            <strong>{selectedRows.length}</strong> patient(s).
          </p>
          <SelectedPatientsList selectedRows={selectedRows} />
          <label className="patient-outreach-action-modal__field">
            New outreach date (applies to every selected patient):{" "}
            <input
              type="date"
              value={newDate}
              min={todayIso}
              max={maxIso}
              onChange={e => setNewDate(e.target.value)}
            />
          </label>
          <p className="patient-outreach-action-modal__hint">
            Postponed rows will be removed from today&apos;s pool. Patients on
            an active call are skipped (HTTP 409 guardrail).
          </p>
          <div className="patient-outreach-action-modal__footer">
            <button type="button" onClick={handleClose} disabled={submitting}>
              Cancel
            </button>
            <button
              type="button"
              className="patient-outreach-action-modal__primary"
              onClick={handleConfirm}
              disabled={submitting || !newDate}
            >
              {submitting ? "Postponing…" : "Postpone"}
            </button>
          </div>
        </>
      ) : (
        <>
          <BulkResultsSummary result={result} />
          <div className="patient-outreach-action-modal__footer">
            <button
              type="button"
              className="patient-outreach-action-modal__primary"
              onClick={handleClose}
            >
              Close
            </button>
          </div>
        </>
      )}
    </ActionModal>
  );
};

BulkPostponeModal.propTypes = {
  selectedRows: PropTypes.array.isRequired,
  onClose: PropTypes.func.isRequired,
  onComplete: PropTypes.func
};

export default BulkPostponeModal;
